// MenuItemsGenerator.jsx
import React from "react";
import { Tag } from "antd";
import { DownOutlined, RightOutlined } from "@ant-design/icons";

const parseMenuKey = (key) => {
  const raw = String(key || "");
  const [path, queryString = ""] = raw.split("?");
  const params = new URLSearchParams(queryString);
  const paramEntries = Array.from(params.entries());
  return { raw, path: path || "", params, paramEntries, paramCount: paramEntries.length };
};

const isKeyMatchForLocation = (routeKey, pathname, searchParams) => {
  const route = parseMenuKey(routeKey);
  if (!route.path) return false;
  const pathMatches = pathname === route.path || pathname.startsWith(route.path + "/");
  if (!pathMatches) return false;
  for (const [k, v] of route.paramEntries) {
    if (searchParams.get(k) !== v) return false;
  }
  return true;
};

export const resolveSelectedMenuKey = (routes, pathname, search) => {
  const safePathname = String(pathname || "");
  const searchParams = new URLSearchParams(String(search || ""));

  let bestKey = null;
  let bestScore = -1;

  const visit = (items) => {
    for (const route of items || []) {
      if (!route || route.type === "divider" || route.isSectionHeader === true) continue;
      if (route.key != null && isKeyMatchForLocation(route.key, safePathname, searchParams)) {
        const parsed = parseMenuKey(route.key);
        const exactPath = safePathname === parsed.path;
        const score = (exactPath ? 10000 : 0) + parsed.path.length * 10 + parsed.paramCount * 100;
        if (score > bestScore) {
          bestScore = score;
          bestKey = parsed.raw;
        }
      }
      if (Array.isArray(route.children) && route.children.length) {
        visit(route.children);
      }
    }
  };

  visit(Array.isArray(routes) ? routes : []);
  return bestKey || safePathname;
};

export const generateMenuItems = (
  routes,
  collapsed,
  theme,
  selectedKey,
  level = 0,
) => {
  if (!Array.isArray(routes)) {
    console.warn(
      "routes is not an array or is undefined, returning empty array"
    );
    return [];
  }

  let rotasFiltradas = routes.filter((x) => !x.hidden);

  return rotasFiltradas.map((route, index) => {
    if (route?.type === "divider") {
      return { type: "divider" };
    }
    const isSubItem = level > 0;
    const isSelected = (
      selectedKey === route.key ||
      (typeof selectedKey === "string" && selectedKey.startsWith(route.key + "/"))
    );
    
    // Usar as cores adaptadas da sidebar se disponíveis, caso contrário fallback para textPrimary
    const itemTextColor = isSelected
      ? (theme.menuItemSelectedText || theme.textSecondary)
      : (theme.menuItemText || theme.textSecondary || theme.textSecondary);

    const iconStyle = {
      transition: "all 0.2s ease",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    };

    const isSectionHeader = route.isSectionHeader === true;
    const sectionIndicator = isSectionHeader
      ? (route.sectionCollapsed ? <RightOutlined /> : <DownOutlined />)
      : null;

    const itemPaddingX = collapsed ? 0 : (isSectionHeader ? 8 : 12);

    const itemStyle = {
      borderRadius: isSectionHeader ? 0 : 8,
      background: isSectionHeader
        ? "transparent"
        : (isSelected ? (theme.menuItemSelectedBackground || "transparent") : "transparent"),
      boxShadow: "none",
      transition: "all 0.2s ease",
      position: "relative",
      color: itemTextColor,
      fontWeight: isSectionHeader ? "600" : (isSelected ? "600" : "500"),
      border: "none",
      margin: isSectionHeader ? "10px 0 6px" : "2px 0",
      minHeight: isSectionHeader ? 28 : (isSubItem ? 36 : 40),
      lineHeight: isSectionHeader ? "28px" : (isSubItem ? "36px" : "40px"),
      paddingLeft: itemPaddingX,
      paddingRight: itemPaddingX,
    };

    const appliedIcon = route.icon
      ? React.cloneElement(route.icon, {
          color: itemTextColor,
          size: 18,
          weight: isSelected ? "bold" : "regular",
          style: iconStyle,
        })
      : (isSectionHeader && collapsed
        ? (
            <span style={{ display: "flex", alignItems: "center", justifyContent: "center", width: 18, height: 18 }}>
              {sectionIndicator}
            </span>
          )
        : null);

    const baseLabel = isSectionHeader
      ? (collapsed
        ? (
            <span
              style={{
                color: itemTextColor,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                fontSize: 11,
                fontWeight: 600,
                textTransform: "uppercase",
                letterSpacing: 0.2,
                opacity: 0.9,
              }}
            >
              {route.label}
            </span>
          )
        : (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                width: "100%",
                gap: 8,
              }}
            >
              <span
                style={{
                  color: itemTextColor,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  fontSize: 11,
                  fontWeight: 600,
                  textTransform: "uppercase",
                  letterSpacing: 0.2,
                  opacity: 0.9,
                }}
              >
                {route.label}
              </span>
              <span
                style={{
                  display: "flex",
                  alignItems: "center",
                  flexShrink: 0,
                  color: itemTextColor,
                  opacity: 0.85,
                  fontSize: 12,
                }}
              >
                {sectionIndicator}
              </span>
            </div>
          ))
      : (
          <span
            style={{
              color: itemTextColor,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              fontSize: isSubItem ? 13 : 14,
              opacity: isSubItem ? 0.95 : 1,
            }}
          >
            {route.label}
          </span>
        );

    const labelWithTag = !isSectionHeader && route.tag ? (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', gap: 8 }}>
        <span style={{ color: itemTextColor, opacity: 0.9, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{route.label}</span>
        <Tag 
          color="blue" 
          bordered={false} 
          style={{ 
            margin: 0, 
            fontSize: 10, 
            lineHeight: '16px',
            backgroundColor: 'rgba(255,255,255,0.2)',
            color: itemTextColor,
            fontWeight: 600
          }}
        >
          {route.tag}
        </Tag>
      </div>
    ) : baseLabel;

    if (route.children) {
      return {
        key: route.key,
        icon: appliedIcon,
        label: labelWithTag,
        children: generateMenuItems(
          route.children,
          collapsed,
          theme,
          selectedKey,
          level + 1,
        ),
        style: { ...itemStyle },
        className: isSectionHeader ? "menu-section-header" : (isSelected ? "menu-item-selected" : "menu-item"),
        disabled: route.disabled,
      };
    }

    return {
      key: route.key,
      icon: appliedIcon,
      label: labelWithTag,
      onClick: route.onClick,
      style: { ...itemStyle },
      className: isSectionHeader ? "menu-section-header" : (isSelected ? "menu-item-selected" : "menu-item"),
      disabled: route.disabled,
    };
  });
};

export default generateMenuItems;
